import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuArrowRight } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import './RecommendedSetup.css';

const PHONES = [
  {
    name: 'Google Pixel 4a',
    price: '$70–$100',
    tag: 'Best overall',
    why: 'Stock Android, no manufacturer skin killing background services. Pixel modem is excellent. Software updates ended in 2023, but for an SMS gateway that doesn\'t matter.'
  },
  {
    name: 'Samsung Galaxy A13 / A14',
    price: '$50–$90',
    tag: 'Most common',
    why: 'Cheapest reliable option. One UI plays nice with the battery-optimization whitelist. A14 still gets Samsung security updates. Dual SIM on most variants.'
  },
  {
    name: 'Motorola Moto G Power (2021 / 2022)',
    price: '$50–$80',
    tag: 'Big battery',
    why: 'Near-stock Android, 5,000 mAh battery survives short power cuts. Clean Motorola UI without Xiaomi/OnePlus-style doze killers.'
  },
  {
    name: 'Nokia G20 / G50',
    price: '$40–$70',
    tag: 'Cheapest',
    why: 'Old Android One pedigree means stock Android with zero manufacturer interference. Slightly weaker radio, but a steal under $50.'
  }
];

const LOOK_FOR = [
  { strong: 'Android 12 or newer.', text: 'Older phones miss Play Protect updates and get flagged at install.' },
  { strong: 'Stock or near-stock Android.', text: 'Pixel, Motorola, Nokia. Less custom skinning means fewer broken background services.' },
  { strong: 'Physical SIM slot.', text: 'You\'ll likely swap SIMs across phones over time. eSIM-only locks you in.' },
  { strong: 'Always-plugged ready.', text: 'Any modern Android handles being plugged in 24/7. Battery condition matters less than radio quality.' },
  { strong: 'Carrier-unlocked.', text: 'Buy unlocked or pay the unlock fee. Locked phones can\'t run prepaid SIMs from competitors.' }
];

const AVOID = [
  { strong: 'Xiaomi, Redmi, Poco.', text: 'MIUI / HyperOS aggressively kills background apps even with battery whitelisting.' },
  { strong: 'Older Huawei (post-2019).', text: 'No Google Mobile Services means no FCM. The phone won\'t receive wake-up pings.' },
  { strong: 'OnePlus / Oppo / Vivo (older models).', text: 'OxygenOS / ColorOS battery management is unreliable for 24/7 background work.' },
  { strong: 'Anything below Android 9.', text: 'Outdated TLS, missing security APIs, frequent Play Protect blocks.' },
  { strong: 'iPhones.', text: 'iOS doesn\'t allow apps to send SMS programmatically. TextLink is Android-only.' }
];

const SIM_POINTS = [
  {
    h: 'Unlimited or high-cap SMS plan',
    p: 'Avoid pay-per-SMS. You want a flat-rate plan with thousands of texts included — the math only works if SMS is effectively free.'
  },
  {
    h: 'Postpaid > prepaid',
    p: 'Postpaid SIMs from major carriers (T-Mobile, Verizon, AT&T) get fewer carrier filters than prepaid MVNOs.'
  },
  {
    h: 'Match the area code to your audience',
    p: 'Local-presence dialing matters. Same-area-code SMS delivers more reliably and gets higher reply rates.'
  },
  {
    h: 'One SIM ≈ 150–250 SMS/day',
    p: 'Carriers throttle individual numbers above this. Built-in warmup helps you ramp gradually so you stay under the radar.'
  }
];

// Per-country deep-dive guides. When a guide exists for the selected
// country we surface a contextual "Read the full guide" link below the
// carriers table. Add slugs here as new country guides ship.
const COUNTRY_GUIDES = {
  US: {
    slug: 'best-sim-card-for-sms-marketing-united-states',
    label: 'US carrier guide'
  }
  // AU, UK, CA, FR, DE — coming soon
};

// Real delivery rates from TextLink's network. Curated from production
// SMS data — only carriers with >=100 resolved sends, generic SIM names
// (CARD 1, SIM1, etc.) filtered out. Update periodically by re-running
// the per-SIM deliverability query in admin SQL.
const CARRIERS_BY_COUNTRY = {
  US: [
    { name: 'Boost',            pct: 99.94 },
    { name: 'TracFone',         pct: 99.89 },
    { name: 'Google Fi',        pct: 99.48 },
    { name: 'Tello',            pct: 99.47 },
    { name: 'Verizon Wireless', pct: 99.07 },
    { name: 'Verizon',          pct: 98.15 },
    { name: 'Cricket',          pct: 97.45 },
    { name: 'T-Mobile',         pct: 97.02 },
    { name: 'AT&T',             pct: 83.82 },
    { name: 'Mint',             pct: 75.31 }
  ],
  CA: [
    { name: 'Bell',          pct: 100.00 },
    { name: 'Lucky Mobile',  pct: 100.00 },
    { name: 'chatr',         pct: 100.00 },
    { name: 'Virgin Plus',   pct: 99.68 },
    { name: 'Public Mobile', pct: 99.21 }
  ],
  GB: [
    { name: '3 (Three UK)',  pct: 100.00 },
    { name: 'Talkmobile',    pct: 100.00 },
    { name: 'O2',            pct: 99.13 }
  ],
  AU: [
    { name: 'Optus',         pct: 100.00 },
    { name: 'EzySim',        pct: 100.00 },
    { name: 'AussieBB',      pct: 99.98 },
    { name: 'Coles Mobile',  pct: 99.97 },
    { name: 'Kogan',         pct: 99.76 },
    { name: 'felix',         pct: 99.71 },
    { name: 'ALDImobile',    pct: 98.32 },
    { name: 'TeleChoice',    pct: 89.86 },
    { name: 'LycaMobile',    pct: 79.90 },
    { name: 'Vodafone AU',   pct: 13.16 }
  ],
  FR: [
    { name: 'Orange',        pct: 100.00 },
    { name: 'Free Mobile',   pct: 100.00 }
  ],
  DE: [
    { name: 'sim24',         pct: 100.00 },
    { name: 'sim.de',        pct: 99.92 },
    { name: 'PremiumSIM',    pct: 97.27 }
  ],
  IE: [
    { name: '3 (Three IE)',  pct: 97.67 }
  ],
  IT: [
    { name: 'TIM',           pct: 97.99 }
  ],
  NL: [
    { name: 'simyo',         pct: 97.06 }
  ],
  NO: [
    { name: 'Happybytes',    pct: 100.00 }
  ],
  CH: [
    { name: 'Swisscom',      pct: 100.00 }
  ],
  PL: [
    { name: 'Play',          pct: 81.95 }
  ],
  UA: [
    { name: 'KYIVSTAR',      pct: 97.63 },
    { name: 'Vodafone UA',   pct: 20.35 }
  ],
  PH: [
    { name: 'GLOBE',         pct: 97.80 },
    { name: 'SMART',         pct: 96.71 },
    { name: 'TM',            pct: 95.17 },
    { name: 'GOMO',          pct: 63.54 }
  ],
  IN: [
    { name: 'airtel',        pct: 96.70 },
    { name: 'Jio',           pct: 28.83 }
  ],
  NZ: [
    { name: 'One NZ',        pct: 93.28 },
    { name: '2degrees',      pct: 81.57 }
  ],
  TH: [
    { name: 'dtac',          pct: 100.00 }
  ],
  TT: [
    { name: 'Digicel',       pct: 100.00 }
  ],
  EG: [
    { name: 'Vodafone EG',   pct: 36.01 }
  ],
  DZ: [
    { name: 'Ooredoo',       pct: 83.92 }
  ],
  AF: [
    { name: 'ATOMA',         pct: 98.60 },
    { name: 'ROSHAN',        pct: 89.53 },
    { name: 'etisalat',      pct: 82.72 },
    { name: 'AWCC',          pct: 78.80 }
  ],
  NG: [
    { name: 'Airtel NG',     pct: 98.37 }
  ]
};

const detectUserCountry = () => {
  try {
    const lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
    return new Intl.Locale(lang).region || null;
  } catch {
    return null;
  }
};

const getCountryName = (code) => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
};

const gradeFor = (pct) => {
  if (pct >= 98) return { label: 'Excellent', cls: 'rs-grade--excellent' };
  if (pct >= 95) return { label: 'Good',      cls: 'rs-grade--good' };
  if (pct >= 80) return { label: 'Fair',      cls: 'rs-grade--fair' };
  return            { label: 'Avoid',         cls: 'rs-grade--poor' };
};

export default function RecommendedSetup() {
  const [country, setCountry] = useState(() => {
    const detected = detectUserCountry();
    return CARRIERS_BY_COUNTRY[detected] ? detected : 'US';
  });

  const countryOptions = useMemo(() => {
    const codes = Object.keys(CARRIERS_BY_COUNTRY);
    const detected = detectUserCountry();
    const head = detected && CARRIERS_BY_COUNTRY[detected] ? [detected] : [];
    const rest = codes
      .filter((c) => c !== head[0])
      .sort((a, b) => getCountryName(a).localeCompare(getCountryName(b)));
    return [...head, ...rest];
  }, []);

  const carriers = CARRIERS_BY_COUNTRY[country] || [];

  return (
    <>
      <Head
        title="Best SIMs & Android Phones for SMS Marketing"
        description="Picking the right phone and SIM for SMS marketing. Real delivery-rate data from TextLink's network — by carrier, by country. Sub-$100 used Android picks that actually work."
        canonical="/recommended-setup"
      />
      <HeaderOne />

      <main className="rs">
        <div className="rs-shell">

          <header className="rs-hero">
            <span className="rs-eyebrow">Setup guide</span>
            <h1 className="rs-h1">Picking the right phone and SIM</h1>
            <p className="rs-lead">
              You don't need a flagship — or anything new. A $60 used Android with a decent radio and a SIM/eSIM will do the job. Here's what we see actually delivering.
            </p>
          </header>

          {/* ---- CARRIERS (moved up: most actionable info) ---- */}
          <section className="rs-section">
            <h2 className="rs-h2">Carrier delivery rates</h2>
            <p className="rs-sub">
              Real numbers from our network — delivered vs. failed, last 60 days.</p>

            <div className="rs-carriers">
              <div className="rs-carriers-controls">
                <label className="rs-country-label" htmlFor="rs-country">Country</label>
                <select
                  id="rs-country"
                  className="rs-country-select"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  {countryOptions.map((c) => (
                    <option key={c} value={c}>{getCountryName(c)}</option>
                  ))}
                </select>
              </div>

              {carriers.length === 0 ? (
                <p className="rs-empty">No data yet for this country.</p>
              ) : (
                <table className="rs-table">
                  <thead>
                    <tr>
                      <th>Carrier</th>
                      <th className="rs-num">Delivered</th>
                      <th>Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carriers.map((c) => {
                      const g = gradeFor(c.pct);
                      return (
                        <tr key={c.name}>
                          <td className="rs-carrier-name">{c.name}</td>
                          <td className="rs-num">{c.pct.toFixed(2)}%</td>
                          <td><span className={`rs-grade ${g.cls}`}>{g.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Surface the country-specific deep-dive guide if one exists,
                  otherwise route to the guides hub. New country guides drop
                  into COUNTRY_GUIDES at the top of this file. */}
              {COUNTRY_GUIDES[country] ? (
                <Link to={`/${COUNTRY_GUIDES[country].slug}`} className="rs-deep-link">
                  Read the full {COUNTRY_GUIDES[country].label}
                  <LuArrowRight size={13} strokeWidth={2} />
                </Link>
              ) : (
                <Link to="/guides" className="rs-deep-link">
                  Browse all carrier guides
                  <LuArrowRight size={13} strokeWidth={2} />
                </Link>
              )}

              <p className="rs-foot">
                Don't see your country? Pick any carrier with an unlimited-SMS plan from a major operator and run a short warmup before scaling.
              </p>
            </div>
          </section>

          {/* ---- PHONES (as a table) ---- */}
          <section className="rs-section">
            <h2 className="rs-h2">Phones we recommend</h2>
            <p className="rs-sub">
              All under $100 used on eBay, Swappa, or Facebook Marketplace. Modern Android, proven in the field.
            </p>

            <table className="rs-table rs-table--phones">
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Used price</th>
                  <th>Tag</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {PHONES.map((p) => (
                  <tr key={p.name}>
                    <td className="rs-phone-name">{p.name}</td>
                    <td className="rs-num">{p.price}</td>
                    <td><span className="rs-pill">{p.tag}</span></td>
                    <td className="rs-phone-why">{p.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* ---- LOOK FOR / AVOID ---- */}
          <section className="rs-section">
            <h2 className="rs-h2">Look for this. Avoid that.</h2>
            <p className="rs-sub">
              Quick checklist if you're picking up a phone off eBay or Marketplace.
            </p>

            <div className="rs-cols">
              <div className="rs-col">
                <h3 className="rs-col-h rs-col-h--good">Look for</h3>
                <ul className="rs-list">
                  {LOOK_FOR.map((i, idx) => (
                    <li key={idx}><strong>{i.strong}</strong> {i.text}</li>
                  ))}
                </ul>
              </div>
              <div className="rs-col">
                <h3 className="rs-col-h rs-col-h--bad">Avoid</h3>
                <ul className="rs-list">
                  {AVOID.map((i, idx) => (
                    <li key={idx}><strong>{i.strong}</strong> {i.text}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* ---- SIM PLAN ADVICE ---- */}
          <section className="rs-section">
            <h2 className="rs-h2">Picking a SIM plan</h2>
            <p className="rs-sub">
              Carrier reputation, plan structure, and local presence matter more than the device.
            </p>

            <div className="rs-points">
              {SIM_POINTS.map((s, i) => (
                <div key={i} className="rs-point">
                  <h4>{s.h}</h4>
                  <p>{s.p}</p>
                </div>
              ))}
            </div>

            <div className="rs-callout">
              <strong>Tip.</strong> SMS-only prepaid plans are dirt cheap — Tello in the US is around $5/month for unlimited SMS, similar deals exist on most carriers globally. You don't need a $25–$40 unlimited-everything plan if you're just sending texts.
            </div>
          </section>

          {/* ---- CTA ---- */}
          <section className="rs-cta">
            <h2 className="rs-h2">Have the phone? Take it from here.</h2>
            <p className="rs-sub">Five minutes from QR scan to first SMS sent.</p>
            <Link to="/signup" className="rs-btn">
              Start sending free <LuArrowRight size={16} strokeWidth={2} />
            </Link>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
